import { useState } from 'react'
import { useAsync } from '@/lib/hooks'
import { useSelectedOrg } from '@/context/SelectedOrgContext'
import { api } from '@/lib/api'
import { useToast } from '@/context/ToastContext'
import { SectionLabel } from '@/shared/components/SectionLabel'
import { formatDate } from '@/lib/format'
import { PageLoading } from '@/shared/components/EmptyState'
import { FeedbackMessage } from '@/shared/components/FeedbackMessage'
import { Modal } from '@/shared/components/Modal'

import { TIERS, TIER_LABEL, TIER_PRICE, TIER_PLAN, BILLABLE_USER_NOTE, formatPence, type Tier } from '@/lib/tiers'

interface BillingInfo {
  tier: Tier
  is_personal: boolean
  limits: {
    modules: number | null
    users: number | null
    includedUsers: number | null
    extraUserPencePerMonth: number | null
    botTokens: number | null
    opa: boolean
    auditLog: boolean
    sso: boolean
    slackNotifications: boolean
    providerRegistry: boolean
    providerServe: boolean
    providerScanning: boolean
    providerBuilds: boolean
    publicEndpointDiscovery: boolean
    maxVersionsPerModule: number | null
    stateBackend: boolean
  }
  usage: {
    modules: number
    users: number
  }
  /** The seat line for the current UTC month. See BILLABLE_USER_NOTE for what `active` counts. */
  seats: {
    period: { start: number; end: number }
    included: number | null
    hard_limit: number | null
    active: number
    billable_extra: number
    extra_user_pence: number | null
    estimated_overage_pence: number
    metered: boolean
  }
  grace_period: {
    active: boolean
    ends_at: number
  } | null
  has_stripe: boolean
}

// ── Upgrade modal ─────────────────────────────────────────────────────────────

const UPGRADE_TIERS: Array<{ tier: Tier; price: string }> = [
  { tier: 'team',       price: '£24/mo' },
  { tier: 'business',   price: '£149/mo' },
  { tier: 'enterprise', price: 'From £249/mo' },
]

function UpgradeModal({ currentTier, orgId, onClose, onUpgraded }: { currentTier: Tier; orgId: string; onClose: () => void; onUpgraded: (tier: Tier) => void }) {
  const [selected, setSelected] = useState<Tier>(() => {
    if (currentTier === 'starter') return 'team'
    if (currentTier === 'team') return 'business'
    return 'enterprise'
  })
  const [busy, setBusy] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)

  const availableTiers = UPGRADE_TIERS.filter(({ tier }) => TIERS.indexOf(tier) > TIERS.indexOf(currentTier))

  async function handleConfirm() {
    setBusy(true)
    setApiError(null)
    try {
      await api.post(`/api/orgs/${orgId}/billing/beta-upgrade`, { tier: selected })
      onUpgraded(selected)
    } catch (err) {
      setApiError((err as Error).message)
      setBusy(false)
    }
  }

  return (
    <Modal track="org_upgrade_plan" title="Upgrade plan" onClose={onClose} maxWidth={520}>
      <div style={{ background: 'var(--accent-d)', border: '1px solid color-mix(in srgb, var(--accent-fill) 20%, transparent)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: '0.8rem', color: 'var(--text2)', lineHeight: 1.5 }}>
        Upgrade instantly, no card required.
      </div>
      {/* Tier selector */}
      <div role="radiogroup" aria-label="Plan" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
        {availableTiers.map(({ tier, price }) => {
          const active = selected === tier
          return (
            <button
              key={tier}
              type="button"
              role="radio"
              aria-checked={active}
              className="tier-card"
              onClick={() => setSelected(tier)}
              style={{ padding: '14px 16px' }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                <span style={{ fontFamily: 'var(--head)', fontSize: '0.95rem', color: 'var(--text)' }}>
                  {TIER_LABEL[tier]}
                </span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: '0.78rem', color: 'var(--accent)' }}>
                  {price}
                </span>
                {active && (
                  <span style={{ marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: '0.65rem', color: 'var(--accent)' }}>
                    selected
                  </span>
                )}
              </div>
              <ul style={{ margin: 0, padding: '0 0 0 0', listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                {TIER_PLAN[tier].features.map(({ label: f }) => (
                  <li key={f} style={{ fontSize: '0.72rem', color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ color: 'var(--green)', fontFamily: 'var(--mono)', fontSize: '0.65rem' }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>
            </button>
          )
        })}
      </div>

      {apiError && <div className="feedback-error" style={{ marginBottom: 14 }}>{apiError}</div>}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" disabled={busy} onClick={handleConfirm}>
          {busy ? <span className="spinner" /> : `Upgrade to ${TIER_LABEL[selected]}`}
        </button>
      </div>
    </Modal>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function OrgBilling({ orgId, canAdmin }: { orgId: string; canAdmin: boolean }) {
  const billing = useAsync<BillingInfo>(() => api.get(`/api/orgs/${orgId}/billing`), [orgId])
  const { showToast } = useToast()
  const { refetchOrgs } = useSelectedOrg()
  const [showUpgrade, setShowUpgrade] = useState(false)

  if (billing.loading) return <PageLoading />
  if (billing.error)   return <FeedbackMessage ok={false} msg={billing.error} />
  if (!billing.data)   return null

  const { tier, is_personal, limits, usage, seats, grace_period } = billing.data
  const canUpgrade = canAdmin && !is_personal && tier !== 'enterprise'

  return (
    <div style={{ maxWidth: 560 }}>
      {showUpgrade && (
        <UpgradeModal
          currentTier={tier}
          orgId={orgId}
          onClose={() => setShowUpgrade(false)}
          onUpgraded={(newTier) => {
            setShowUpgrade(false)
            billing.refetch()
            refetchOrgs()
            showToast(`Plan upgraded to ${TIER_LABEL[newTier]}`, 'success')
          }}
        />
      )}

      {grace_period && (
        <div style={{ background: 'var(--amber-d)', border: '1px solid var(--amber)', borderRadius: 6, padding: '10px 14px', marginBottom: 20, fontSize: '0.82rem', color: 'var(--amber)' }}>
          {grace_period.active
            ? `Downgrade grace period - excess modules are read-only until ${formatDate(grace_period.ends_at)}. Delete modules to restore full access.`
            : 'Your grace period has ended. Excess modules are locked. Delete down to your plan limit to restore access.'}
        </div>
      )}

      {/* Current plan */}
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <SectionLabel>Current plan</SectionLabel>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontFamily: 'var(--head)', fontSize: '1.05rem' }}>{TIER_LABEL[tier]}</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem', color: 'var(--accent)' }}>{TIER_PRICE[tier]}</span>
          </div>
          {is_personal && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text3)', marginTop: 4 }}>
              Personal workspaces are always on the free Solo plan. Create an org to upgrade.
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canUpgrade && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowUpgrade(true)}>
              Upgrade
            </button>
          )}
        </div>
      </div>


      {/* Usage */}
      <div style={{ marginBottom: 20 }}>
        <SectionLabel>Usage</SectionLabel>
        <UsageMeter label="Modules" used={usage.modules} limit={limits.modules} />
        <UsageMeter label="Members"    used={usage.users}      limit={limits.users} />
      </div>

      <SeatsPanel seats={seats} />

      {/* Features */}
      <div>
        <SectionLabel>Plan features</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
          <FeatureRow label="State backend"             enabled={limits.stateBackend} />
          <FeatureRow label="OPA policy reporting"      enabled={true} />
          <FeatureRow label="OPA enforcement"           enabled={limits.opa} />
          <FeatureRow label="SSO (OIDC)"                enabled={limits.sso} />
          <FeatureRow label="Slack notifications"       enabled={limits.slackNotifications} />
          <FeatureRow label="Provider management"       enabled={limits.providerRegistry} />
          <FeatureRow label="Provider serving"          enabled={limits.providerServe} />
          <FeatureRow label="Provider scanning"         enabled={limits.providerScanning} />
          <FeatureRow label="Provider builds & SBOM"    enabled={limits.providerBuilds} />
          <FeatureRow label="Public endpoint discovery" enabled={limits.publicEndpointDiscovery} />
          <FeatureRow label="Audit log"                 enabled={limits.auditLog} />
          <ValueRow   label="Versions per module"       value={limits.maxVersionsPerModule === null ? 'Unlimited' : `${limits.maxVersionsPerModule} (rolling)`} />
          <ValueRow   label="Bot tokens"                value={limits.botTokens === null ? 'Unlimited' : String(limits.botTokens)} />
        </div>
      </div>
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

/**
 * The seat line: billable users this month against the tier's included allowance, and what the
 * overage would come to. `active` is who has done something this month, not who is a member, so
 * it can sit below the Members meter above it; that gap is the idle members, and they are free.
 */
function SeatsPanel({ seats }: { seats: BillingInfo['seats'] }) {
  const monthLabel = new Date(seats.period.start).toLocaleString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })
  const hasOverage = seats.included !== null && seats.extra_user_pence !== null
  const overLimit  = hasOverage && seats.billable_extra > 0

  return (
    <div style={{ marginBottom: 20 }}>
      <SectionLabel>Billable users · {monthLabel}</SectionLabel>
      {seats.included === null ? (
        <div style={{ fontSize: '0.8rem', color: 'var(--text2)', marginBottom: 6 }}>
          <span style={{ fontFamily: 'var(--mono)' }}>{seats.active}</span> active this month. User count is set by your contract.
        </div>
      ) : (
        <UsageMeter label="Active this month" used={seats.active} limit={seats.included} />
      )}
      {hasOverage && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: overLimit ? 'var(--amber)' : 'var(--text2)', marginBottom: 6 }}>
          <span>
            {seats.included} included
            {overLimit
              ? ` · ${seats.billable_extra} extra × ${formatPence(seats.extra_user_pence!)}`
              : ` · ${formatPence(seats.extra_user_pence!)}/mo per user after that`}
          </span>
          {overLimit && (
            <span style={{ fontFamily: 'var(--mono)' }}>
              {formatPence(seats.estimated_overage_pence)} {seats.metered ? 'so far this month' : 'estimated, not charged'}
            </span>
          )}
        </div>
      )}
      {hasOverage && !seats.metered && !overLimit && (
        <div style={{ fontSize: '0.72rem', color: 'var(--text3)', marginBottom: 6 }}>
          Overage is not being charged yet. The count is shown so there are no surprises when it is.
        </div>
      )}
      <div style={{ fontSize: '0.72rem', color: 'var(--text3)', lineHeight: 1.5 }}>
        {BILLABLE_USER_NOTE}
      </div>
    </div>
  )
}

function UsageMeter({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  const pct       = limit !== null ? Math.min(100, Math.round((used / limit) * 100)) : 0
  const nearLimit = limit !== null && used / limit >= 0.8
  const atLimit   = limit !== null && used >= limit
  const color     = atLimit ? 'var(--red)' : nearLimit ? 'var(--amber)' : 'var(--accent)'

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: '0.8rem', color: 'var(--text2)' }}>{label}</span>
        <span style={{ fontFamily: 'var(--mono)', fontSize: '0.78rem', color: atLimit ? 'var(--red)' : nearLimit ? 'var(--amber)' : 'var(--text2)' }}>
          {used}{limit !== null ? ` / ${limit}` : ''}
        </span>
      </div>
      {limit !== null && (
        <div style={{ height: 3, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 2, transition: 'width 0.3s' }} />
        </div>
      )}
    </div>
  )
}

function FeatureRow({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', padding: '3px 0' }}>
      <span style={{ color: enabled ? 'var(--green)' : 'var(--text3)', fontFamily: 'var(--mono)', fontSize: '0.7rem', minWidth: 10 }}>
        {enabled ? '✓' : '✗'}
      </span>
      <span style={{ color: enabled ? 'var(--text2)' : 'var(--text3)' }}>{label}</span>
    </div>
  )
}

function ValueRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', padding: '3px 0' }}>
      <span style={{ color: 'var(--accent)', fontFamily: 'var(--mono)', fontSize: '0.7rem', minWidth: 10 }}>→</span>
      <span style={{ color: 'var(--text2)' }}>{label}</span>
      <span style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--text)', marginLeft: 'auto' }}>{value}</span>
    </div>
  )
}

